import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Eraser, Loader2, RefreshCw, Undo2, ShieldCheck } from "lucide-react";

const TruckBoxes = ({ t }) => (
  <div data-testid={`worked-out-truck-${t.stock_number}`} className="px-3 py-2.5">
    <div className="flex flex-wrap items-center gap-2">
      <Link to={`/donors/${t.donor_id}`} className="font-mono font-bold text-sm text-[#B01E28] hover:underline">{t.stock_number}</Link>
      <span className="text-sm text-slate-700">{t.title}</span>
    </div>
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {t.boxes.map((b) => (
        <span key={b.key} data-testid={`worked-out-box-${t.stock_number}-${b.key}`} title={b.why}
          className="text-xs rounded-full border border-amber-200 bg-amber-50 text-amber-900 px-2.5 py-1">
          <b>{b.label}</b> <span className="text-amber-700/80">· {b.why}</span>
        </span>
      ))}
    </div>
  </div>
);

const RemovalRow = ({ r, busy, onUndo }) => (
  <div data-testid={`worked-out-removal-${r.stock_number}`} className="px-3 py-2.5 flex flex-wrap items-center gap-2">
    <Link to={`/donors/${r.donor_id}`} className="font-mono font-bold text-sm text-[#B01E28] hover:underline">{r.stock_number}</Link>
    <span className="text-[12px] text-slate-600">{r.boxes.map((b) => b.label).join(", ")}</span>
    {r.undone_at ? (
      <span data-testid={`worked-out-undone-${r.stock_number}`} className="ml-auto text-[11px] font-bold uppercase text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
        put back{r.undo_note ? ` · ${r.undo_note}` : ""}
      </span>
    ) : (
      <button data-testid={`worked-out-undo-${r.stock_number}`} disabled={busy} onClick={() => onUndo(r)}
        className="ml-auto shrink-0 inline-flex items-center gap-1 rounded-lg border border-slate-300 hover:border-[#B01E28] hover:text-[#B01E28] text-xs font-bold px-2.5 h-8 transition-colors">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}Undo this truck
      </button>
    )}
  </div>
);

// WORKED-OUT VALUES — his ruling 2026-10-20: a value the sheet does not print is not data. Count first,
// he presses go, every truck undoes on its own. Box names only — never the values.
export const WorkedOutPanel = ({ canManage }) => {
  const [data, setData] = useState(null);
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try { setData((await api.get("/catch-up/worked-out")).data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not count the worked-out values"); }
  }, []);
  useEffect(() => { if (canManage) load(); }, [canManage, load]);

  const takeOff = async () => {
    setAsk(false); setBusy("all");
    try {
      const { data: r } = await api.post("/catch-up/worked-out/take-off", { confirm: true, expected_values: data.counts.values });
      toast.success(`${r.values} value(s) taken off ${r.trucks} truck(s) — each one is in its truck's history`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(""); load();
  };
  const undo = async (r) => {
    setBusy(r.id);
    try {
      const { data: u } = await api.post(`/catch-up/worked-out/removals/${r.id}/undo`);
      toast.success(`${r.stock_number}: ${u.undo_note}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(""); load();
  };

  if (!canManage) return null;
  const c = data?.counts || {};
  return (
    <section data-testid="tool-worked-out" className="rounded-xl border-2 border-[#1F1F1F] bg-white overflow-hidden">
      <div className="bg-[#1F1F1F] text-white px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Eraser className="w-5 h-5 shrink-0" />
          <h2 className="font-display text-lg font-bold truncate">Worked-out values</h2>
        </div>
        <button data-testid="worked-out-refresh" onClick={load} title="Count again"
          className="text-[10px] font-mono uppercase tracking-widest bg-white/15 hover:bg-white/25 px-2 py-1 rounded shrink-0 transition-colors">
          <RefreshCw className="w-3 h-3 inline mr-1" />Recount
        </button>
      </div>
      <div className="p-4 space-y-3">
        <p className="text-sm text-slate-700">
          AI values from a build-sheet read that <b>nobody has ticked</b> and that the truck's own sheet <b>does not print</b>
          {" "}— not in full, not abbreviated, not in other units. Blank is better than a guess. Ticked or typed values are never touched.
        </p>
        {!data ? (
          <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Counting…</div>
        ) : (
          <>
            <div data-testid="worked-out-summary" className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900 flex items-start gap-2">
              <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" />
              <span>
                <b data-testid="worked-out-values">{c.values}</b> value(s) on <b data-testid="worked-out-trucks">{c.trucks}</b> truck(s) —
                counted only, nothing changed. {c.trucks_checked} truck(s) checked against their sheet
                {c.trucks_unjudged ? <>; <b data-testid="worked-out-unjudged">{c.trucks_unjudged}</b> left alone — no sheet text stored to check against</> : null}.
              </span>
            </div>
            {data.trucks.length > 0 && (
              <div data-testid="worked-out-list" className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                {data.trucks.map((t) => <TruckBoxes key={t.donor_id} t={t} />)}
              </div>
            )}
            {c.values > 0 && (
              <Button data-testid="worked-out-take-off" onClick={() => setAsk(true)} disabled={!!busy}
                className="rounded-lg h-12 px-5 text-base font-bold bg-[#B01E28] hover:bg-[#8f1820]">
                {busy === "all" ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Eraser className="w-5 h-5 mr-2" />}
                Take them off — kept in each truck's history
              </Button>
            )}
            {data.removals.length > 0 && (
              <div className="rounded-lg border border-slate-200">
                <div className="px-3 py-2 text-xs font-bold uppercase tracking-widest text-slate-400">Taken off — undo per truck</div>
                <div data-testid="worked-out-removals" className="divide-y divide-slate-100 border-t border-slate-100">
                  {data.removals.map((r) => <RemovalRow key={r.id} r={r} busy={busy === r.id} onUndo={undo} />)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <Dialog open={ask} onOpenChange={setAsk}>
        <DialogContent className="rounded-xl sm:max-w-md" data-testid="worked-out-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#B01E28]">Take off {c.values} worked-out value(s)?</DialogTitle>
            <DialogDescription>
              Exactly the {c.values} box(es) listed, on {c.trucks} truck(s). Each value is kept in its truck's history,
              and every truck gets its own Undo here.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-lg h-11" data-testid="worked-out-cancel" onClick={() => setAsk(false)}>Not yet</Button>
            <Button data-testid="worked-out-confirm" onClick={takeOff} className="rounded-lg h-11 bg-[#B01E28] hover:bg-[#8f1820] font-bold">Yes — take them off</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};
